import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './Spinner';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  iconLeading?: ReactNode;
  /** MVP-438: see `IconButton`'s prop of the same name. A button that disables itself *as a result
   * of its own press* — a Reset that greys out once you are at the default — takes the keyboard
   * user's focus down with it. `aria-disabled` keeps the button focusable and announces the same
   * state; the click is swallowed here so it stays inert. */
  keepFocusWhenDisabled?: boolean;
}

/** DESIGN.md §9.1 Button. Loading keeps the label (fixed width, no reflow) and swaps the
 * leading icon for a spinner, per §6.2's rule for a loading button. */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  fullWidth = false,
  iconLeading,
  className,
  children,
  disabled,
  keepFocusWhenDisabled = false,
  onClick,
  ...rest
}: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, size !== 'md' ? `btn--${size}` : '', fullWidth ? 'btn--full' : '', className]
    .filter(Boolean)
    .join(' ');

  const isDisabled = disabled || loading;
  // `loading` is never soft — same reasoning as IconButton's: a busy button must not be re-pressable.
  const soft = keepFocusWhenDisabled && isDisabled && !loading;

  return (
    <button
      className={classes}
      disabled={soft ? undefined : isDisabled}
      aria-disabled={soft || undefined}
      onClick={soft ? undefined : onClick}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="btn__spinner">
          <Spinner size={14} />
        </span>
      ) : (
        iconLeading
      )}
      <span className="text-body">{children}</span>
    </button>
  );
}
